import { useState, useEffect } from "react";
import { CheckCircle, Plus, Trash2, ChevronDown, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function IntakeTab({ token }) {
  const [template, setTemplate] = useState(null);
  const [responses, setResponses] = useState([]);
  const [sporters, setSporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extraVragen, setExtraVragen] = useState([]);
  const [selectedSporter, setSelectedSporter] = useState("");
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [activeView, setActiveView] = useState("template");
  const [selectedResponse, setSelectedResponse] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/company/intake-template`, auth).then(r => r.json()),
      fetch(`${API}/api/company/intake-responses`, auth).then(r => r.json()),
      fetch(`${API}/api/company/sporters`, auth).then(r => r.json()),
    ]).then(([tmpl, resp, sp]) => {
      setTemplate(tmpl);
      setExtraVragen(tmpl?.extra_vragen || []);
      setResponses(Array.isArray(resp) ? resp : []);
      setSporters(Array.isArray(sp) ? sp : (sp?.sporters || []));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addExtraVraag = () => {
    setExtraVragen(v => [...v, { id: `custom_${Date.now()}`, vraag: "", type: "tekst", verplicht: false, opties: [] }]);
  };

  const updateVraag = (idx, field, val) => {
    setExtraVragen(v => v.map((q, i) => i === idx ? { ...q, [field]: val } : q));
  };

  const removeVraag = (idx) => {
    setExtraVragen(v => v.filter((_, i) => i !== idx));
  };

  const saveTemplate = async () => {
    await fetch(`${API}/api/company/intake-template`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...auth.headers },
      body: JSON.stringify({ extra_vragen: extraVragen, actief: true }),
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const sendIntake = async () => {
    if (!selectedSporter) return;
    setSending(true);
    setSendResult(null);
    try {
      const r = await fetch(`${API}/api/company/intake/send/${selectedSporter}`, {
        method: "POST",
        headers: auth.headers,
      });
      const d = await r.json();
      if (r.ok) setSendResult({ ok: true, url: d.form_url });
      else setSendResult({ ok: false, msg: d.detail || "Versturen mislukt" });
    } catch {
      setSendResult({ ok: false, msg: "Netwerk fout" });
    }
    setSending(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Sub-navigatie */}
      <div className="flex gap-2">
        {[
          { key: "template", label: "Formulier & Versturen" },
          { key: "responses", label: `Ingevulde formulieren (${responses.length})` },
        ].map(v => (
          <button
            key={v.key}
            onClick={() => setActiveView(v.key)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeView === v.key
                ? "bg-[#C9A84C] text-black"
                : "bg-[#1a1a1a] text-zinc-500 hover:text-white border border-white/8"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {activeView === "template" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Vaste basisvragen */}
          <div className="bg-[#111] border border-white/8 rounded-2xl p-6">
            <h3 className="text-white font-bold mb-1">Standaard vragen</h3>
            <p className="text-zinc-500 text-xs mb-4">Deze vragen zijn altijd aanwezig in elk intake formulier.</p>
            <ul className="space-y-2">
              {(template?.basis_vragen || []).map((v, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-zinc-400">
                  <div className="w-4 h-4 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/30 flex items-center justify-center shrink-0">
                    <CheckCircle size={9} className="text-[#C9A84C]" />
                  </div>
                  {v.vraag}
                </li>
              ))}
            </ul>
          </div>

          {/* Eigen extra vragen */}
          <div className="bg-[#111] border border-white/8 rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-bold mb-1">Eigen vragen toevoegen</h3>
                <p className="text-zinc-500 text-xs">Voeg bedrijfsspecifieke vragen toe aan het formulier.</p>
              </div>
              <button
                onClick={addExtraVraag}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#C9A84C]/10 border border-[#C9A84C]/25 text-[#C9A84C] text-xs font-bold hover:bg-[#C9A84C]/20"
              >
                <Plus size={12} /> Vraag toevoegen
              </button>
            </div>

            {extraVragen.length === 0 && (
              <p className="text-zinc-600 text-sm text-center py-4">Nog geen eigen vragen</p>
            )}

            {extraVragen.map((q, idx) => (
              <div key={idx} className="bg-[#1a1a1a] border border-white/6 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    value={q.vraag}
                    onChange={e => updateVraag(idx, "vraag", e.target.value)}
                    placeholder="Stel je vraag hier..."
                    className="flex-1 bg-[#111] border border-white/8 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
                  />
                  <button onClick={() => removeVraag(idx)} className="text-zinc-600 hover:text-red-400 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="flex gap-2">
                  {[["tekst", "Tekst"], ["meerkeuze", "Meerkeuze"], ["ja_nee", "Ja/Nee"]].map(([val, lbl]) => (
                    <button
                      key={val}
                      onClick={() => updateVraag(idx, "type", val)}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                        q.type === val ? "bg-[#C9A84C]/20 border border-[#C9A84C]/40 text-[#C9A84C]" : "bg-[#111] border border-white/8 text-zinc-500 hover:text-white"
                      }`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
                {q.type === "meerkeuze" && (
                  <input
                    value={(q.opties || []).join(", ")}
                    onChange={e => updateVraag(idx, "opties", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                    placeholder="Optie 1, Optie 2, Optie 3"
                    className="w-full bg-[#111] border border-white/8 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#C9A84C]/50"
                  />
                )}
              </div>
            ))}

            <button
              onClick={saveTemplate}
              className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${
                saved
                  ? "bg-green-500/15 border border-green-500/30 text-green-400"
                  : "bg-[#C9A84C] text-black hover:bg-[#d4b05c]"
              }`}
            >
              {saved ? "Opgeslagen!" : "Formulier opslaan"}
            </button>
          </div>

          {/* Intake versturen */}
          <div className="lg:col-span-2 bg-[#111] border border-white/8 rounded-2xl p-6">
            <h3 className="text-white font-bold mb-1">Intake versturen naar sporter</h3>
            <p className="text-zinc-500 text-xs mb-4">
              De sporter ontvangt een e-mail met een unieke invullink. Geen login vereist.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={selectedSporter}
                onChange={e => setSelectedSporter(e.target.value)}
                className="flex-1 bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
                data-testid="intake-sporter-select"
              >
                <option value="">Kies een sporter...</option>
                {sporters.map(s => (
                  <option key={s.id} value={s.id}>{s.naam} ({s.email})</option>
                ))}
              </select>
              <button
                onClick={sendIntake}
                disabled={!selectedSporter || sending}
                data-testid="intake-send-btn"
                className="px-6 py-3 rounded-xl bg-[#C9A84C] text-black font-bold text-sm hover:bg-[#d4b05c] disabled:opacity-50 transition-all flex items-center gap-2"
              >
                <Mail size={14} /> {sending ? "Versturen..." : "Intake sturen"}
              </button>
            </div>
            {sendResult && (
              <div className={`mt-3 p-3 rounded-xl text-sm ${sendResult.ok ? "bg-green-500/10 border border-green-500/20 text-green-400" : "bg-red-500/10 border border-red-500/20 text-red-400"}`}>
                {sendResult.ok ? `Verstuurd! Link: ${sendResult.url}` : sendResult.msg}
              </div>
            )}
          </div>
        </div>
      )}

      {activeView === "responses" && (
        <div>
          {responses.length === 0 ? (
            <div className="text-center py-16 text-zinc-600">Nog geen ingevulde intakeformulieren</div>
          ) : (
            <div className="space-y-3">
              {responses.map((r, i) => (
                <div
                  key={i}
                  onClick={() => setSelectedResponse(selectedResponse === i ? null : i)}
                  className="bg-[#111] border border-white/8 rounded-xl p-5 cursor-pointer hover:border-[#C9A84C]/30 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold">{r.sporter_naam}</p>
                      <p className="text-zinc-500 text-xs">{r.sporter_email} · {r.ingevuld_op ? new Date(r.ingevuld_op).toLocaleDateString("nl-NL") : ""}</p>
                    </div>
                    <ChevronDown size={16} className={`text-zinc-500 transition-transform ${selectedResponse === i ? "rotate-180" : ""}`} />
                  </div>
                  {selectedResponse === i && r.antwoorden && (
                    <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
                      {Object.entries(r.antwoorden).map(([k, v]) => (
                        <div key={k} className="flex gap-3 text-sm">
                          <span className="text-zinc-500 min-w-32 shrink-0">{k.replace(/_/g, " ")}</span>
                          <span className="text-white">{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
